import styled from '@emotion/styled';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import entryImage from '../assets/entry.png';
import { formatNumber } from 'utils/numberFormat';
import { isQuickChat } from 'productLine';

const Overview = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 0 0 auto;
  height: 80px;
  padding: 0 22px;
  background: #f2f6ff;
  [data-color-mode='dark'] & {
    background: var(--fett-bg-elevated);
  }
  border-radius: 4px 4px 0 0;
`;

const OverviewLeft = styled.div`
  display: flex;
  align-items: center;
  > img {
    width: 56px;
    height: 56px;
    margin-right: 10px;
    border-radius: 28px;
  }
  > div {
    > h3 {
      margin-bottom: 0;
      font-size: 16px;
      color: var(--theme-text-1);
    }
    > span {
      color: var(--theme-text-2);
    }
  }
`;

type Status = 'success' | 'error' | 'primary';

const OverviewRight = styled.div<{ status: Status }>`
  text-align: right;
  > div {
    font-size: 16px;
    color: var(--ant-${({ status }) => status}-color);
  }
  > span {
    color: var(--theme-text-2);
  }
`;

export interface EntryOverviewProps {
  hasData: boolean;
  enabledTaskCount: number;
  totalTaskCount: number;
}

export const EntryOverview = (props: EntryOverviewProps) => {
  const { hasData, enabledTaskCount, totalTaskCount } = props;
  const { t } = useTranslate();

  const result = useMemo(() => {
    const genResult = (text: string, status: Status) => {
      return { text, status };
    };

    if (enabledTaskCount === totalTaskCount) {
      return genResult(t('aide.太棒了，已完成全部任务的配置'), 'primary');
    }

    if (enabledTaskCount / totalTaskCount >= 0.5) {
      return genResult(
        t('aide.领先x%的卖家', {
          x: formatNumber((enabledTaskCount / totalTaskCount) * 100, '0.0'),
        }),
        'success',
      );
    }

    return genResult(
      t('aide.落后x%的卖家', {
        x: formatNumber((1 - enabledTaskCount / totalTaskCount) * 100, '0.0'),
      }),
      'error',
    );
  }, [t, totalTaskCount, enabledTaskCount]);

  return (
    <Overview style={isQuickChat ? { background: '#F4F3FF' } : undefined}>
      <OverviewLeft>
        <img src={entryImage} alt="entry" />
        <div>
          <h3>{t('aide.跟单场景配置任务')}</h3>
          <span>{t('aide.点击对应环节，按引导完成任务即可提升优化')}</span>
        </div>
      </OverviewLeft>
      {hasData ? (
        <OverviewRight status={result ? result.status : 'primary'}>
          <div>{result ? result.text : ''}</div>
          <span>
            {t('aide.已完成 x 个任务 / 共计 y 个任务 ', {
              x: enabledTaskCount,
              y: totalTaskCount,
            })}
          </span>
        </OverviewRight>
      ) : null}
    </Overview>
  );
};
